import { useQuery } from "@apollo/client";
import { Avatar, Box, Card, CircularProgress, Link, Typography } from "@mui/material";
import { formatDistanceToNow } from "date-fns";
import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { GET_EVENT_COMMENTS } from "../gql/queries/event";
import { Spacing } from "../types/enum";
import { type CommentModel } from "../types/graphql";

const EventCommentsPage: React.FC = () => {
  const { eventId } = useParams<Record<string, string>>();

  const { data, loading } = useQuery(GET_EVENT_COMMENTS, {
    fetchPolicy: "network-only",
    variables: { id: eventId },
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={Spacing.xl}>
        <CircularProgress />
      </Box>
    );
  }

  const comments: CommentModel[] = data?.getEventAdmin?.comments ?? [];

  if (comments.length === 0) {
    return (
      <Card>
        <Box p={Spacing.l}>
          <Typography variant="body2" color="textSecondary">
            No comments on this event yet.
          </Typography>
        </Box>
      </Card>
    );
  }

  return (
    <Card>
      <Box p={Spacing.l}>
        <Typography variant="subtitle1" fontWeight={600} mb={Spacing.m}>
          {comments.length} comment{comments.length === 1 ? "" : "s"}
        </Typography>
        <Box display="flex" flexDirection="column" gap={Spacing.m}>
          {comments.map((comment) => (
            <Box
              key={comment.id}
              display="flex"
              gap={Spacing.m}
              sx={{ pl: comment.parentID ? 5 : 0 }}
            >
              <Avatar src={comment.author?.avatarUrl ?? undefined} sx={{ width: 34, height: 34 }} />
              <Box flex={1}>
                <Box display="flex" alignItems="baseline" gap={1} flexWrap="wrap">
                  {comment.author ? (
                    <Link
                      component={RouterLink}
                      to={`/users/${comment.author.id}`}
                      color="inherit"
                      underline="hover"
                    >
                      <Typography variant="body2" fontWeight={600} component="span">
                        {comment.author.name}
                      </Typography>
                    </Link>
                  ) : (
                    <Typography variant="body2" fontWeight={600} color="textSecondary">
                      Inactive member
                    </Typography>
                  )}
                  {comment.createdAt && (
                    <Typography variant="caption" color="textSecondary">
                      {formatDistanceToNow(new Date(comment.createdAt), { addSuffix: true })}
                    </Typography>
                  )}
                  {comment.parentID && (
                    <Typography variant="caption" color="textSecondary">
                      · reply
                    </Typography>
                  )}
                </Box>
                <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mt: 0.25 }}>
                  {comment.message}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>
      </Box>
    </Card>
  );
};

export default EventCommentsPage;
